import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHero, CtaBanner } from "@/components/PageParts";
import { Film, Camera, Music, Package, Check, ArrowRight } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function LegacyFilms() {
  const [packages, setPackages] = useState([]);
  const [buyingSlug, setBuyingSlug] = useState(null);

  useEffect(() => { api.get("/content/packages").then((r) => setPackages(r.data || [])); }, []);

  const buy = async (slug) => {
    setBuyingSlug(slug);
    try {
      const { data } = await api.post("/checkout/session", {
        origin_url: window.location.origin,
        package_slug: slug,
      });
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err));
      setBuyingSlug(null);
    }
  };

  const steps = [
    { icon: Camera, t: "Pre-Interview", d: "We connect by phone, or send you a thoughtful question guide by email, to understand the person and the arc of the life to be filmed." },
    { icon: Film, t: "Filming Days", d: "Cinematic 4K interviews and B-roll in familiar places — home, garden, kitchen, workshop." },
    { icon: Music, t: "Story Edit", d: "Weeks of gentle editing, archival photos woven in, original music composed to match." },
    { icon: Package, t: "Online 4K Delivery", d: "Your finished film delivered online in stunning 4K, ready to stream and download. A USB keepsake is available as an optional extra." },
  ];

  return (
    <div data-testid="page-legacy-films">
      <PageHero
        eyebrow="Legacy Documentaries & Family History Films"
        title="One life. One story. Made cinematic."
        subtitle="A quiet, patient, professional documentary about the person you love — filmed with warmth and edited with care."
        image="https://images.pexels.com/photos/8848784/pexels-photo-8848784.jpeg"
        testid="legacy-films-hero"
      />

      {/* Process */}
      <section className="section-pad bg-offwhite">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="mb-16 text-center max-w-2xl mx-auto">
            <p className="eyebrow mb-3">The Craft</p>
            <h2 className="font-serif text-4xl md:text-5xl">A process built for patience.</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {steps.map((s, i) => (
              <div key={i} className="bg-white p-10 rounded-xl border border-border-warm">
                <s.icon size={30} style={{ color: "#C9A961" }} className="mb-5" />
                <h3 className="font-serif text-2xl mb-3">{s.t}</h3>
                <p className="text-warmgrey leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="section-pad bg-cream" id="packages">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <p className="eyebrow mb-3">Packages &amp; Pricing</p>
            <h2 className="font-serif text-4xl md:text-5xl mb-5">Choose the film that fits the life you're honouring.</h2>
            <p className="text-warmgrey text-lg">Four tiers, each crafted with the same care. Every package includes cinematic 4K filming, original scoring and gentle unhurried editing.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {packages.map((p) => (
              <div key={p.id} data-testid={`package-${p.slug}`}
                className={`relative bg-white rounded-xl p-8 border-2 transition-all hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(0,0,0,0.06)]
                  ${p.highlighted ? "border-primary shadow-[0_10px_30px_rgba(201,169,97,0.15)]" : "border-border-warm"}`}>
                {p.highlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white text-xs uppercase tracking-widest px-4 py-1 rounded-full">
                    Most chosen
                  </div>
                )}
                <div className="h-1.5 w-12 bg-primary rounded mb-6"></div>
                <h3 className="font-serif text-3xl mb-2">{p.name}</h3>
                <p className="text-warmgrey text-sm min-h-[50px] mb-5">{p.tagline}</p>
                <div className="mb-6">
                  <span className="font-serif text-5xl text-headline">${Math.round(p.price_aud).toLocaleString()}</span>
                  <span className="text-warmgrey text-sm ml-1">AUD</span>
                </div>
                <ul className="space-y-3 mb-8 min-h-[240px]">
                  {(p.features || []).map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-warmgrey">
                      <Check size={16} style={{ color: "#C9A961" }} className="mt-1 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="space-y-3">
                  <button onClick={() => buy(p.slug)} disabled={buyingSlug === p.slug}
                    className={`w-full justify-center ${p.highlighted ? "btn-primary" : "btn-secondary"}`}
                    data-testid={`buy-${p.slug}`}>
                    {buyingSlug === p.slug ? "Redirecting..." : "Secure Your Date"}
                  </button>
                  <Link to="/contact" className="btn-outline w-full justify-center" data-testid={`chat-${p.slug}`}>
                    Book Free Chat <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
          <p className="text-center mt-14 text-warmgrey text-sm max-w-2xl mx-auto">
            All prices in AUD, GST inclusive. Secure your date with full payment. Not sure which package suits you? Book a free 20-minute chat and we'll help you decide.
          </p>
        </div>
      </section>

      <CtaBanner title="Ready to start?" subtitle="Book a free 20-minute chat and we'll help you decide." ctaLabel="Book Free Chat" ctaTo="/contact" testid="legacy-films-cta" />
    </div>
  );
}
